/**
 * Reading a task group's summary: how far it has got, whether it has ended, and
 * whether anything in it failed.
 *
 * The summary reports a count per status string, so an end state is recognised
 * by keyword. One copy of those keywords serves every screen that follows a
 * task group, so a corrected one cannot diverge between them.
 */

export type TaskGroupSummaryItem = { status?: string; count?: number };
export type TaskGroupProgress = { progress: number; title: string; finished: boolean };

const DONE_STATUS_KEYWORDS = ['finished', 'failed', 'aborted', 'skipped', 'limitexceeded', 'limit_exceeded'];
const FAILED_STATUS_KEYWORDS = ['failed', 'aborted', 'limitexceeded', 'limit_exceeded'];

function countMatching(summary: TaskGroupSummaryItem[], keywords: string[]): number {
  return summary.reduce((sum, item) => {
    const normalized = String(item.status || '').toLowerCase();
    return sum + (keywords.some((keyword) => normalized.includes(keyword)) ? item.count || 0 : 0);
  }, 0);
}

function countAll(summary: TaskGroupSummaryItem[]): number {
  return summary.reduce((sum, item) => sum + (item.count || 0), 0);
}

/** True once every task of the group has reached an end state. */
export function isTaskGroupFinished(summary: TaskGroupSummaryItem[] | undefined): boolean {
  if (!summary?.length) {
    return false;
  }

  const total = countAll(summary);
  if (total <= 0) {
    return false;
  }

  return countMatching(summary, DONE_STATUS_KEYWORDS) >= total;
}

/** True when any task of the group ended in a state that did not do its work. */
export function hasTaskGroupFailure(summary: TaskGroupSummaryItem[] | undefined): boolean {
  if (!summary?.length) {
    return false;
  }

  return countMatching(summary, FAILED_STATUS_KEYWORDS) > 0;
}

/**
 * True when the group actually did its work: at least one task finished and
 * none failed. A group whose tasks were all skipped ends without having done
 * anything, and a caller waiting on its output has not got one.
 */
export function didTaskGroupWrite(summary: TaskGroupSummaryItem[] | undefined): boolean {
  if (!summary?.length || hasTaskGroupFailure(summary)) {
    return false;
  }

  return countMatching(summary, ['finished']) > 0;
}

/** How far a task group has got, as a percentage and a sentence. */
export function inferTaskProgress(summary: TaskGroupSummaryItem[] | undefined): TaskGroupProgress {
  if (!summary?.length) {
    return {
      progress: 8,
      title: 'Optimization in progress',
      finished: false,
    };
  }

  const total = countAll(summary);

  if (total <= 0) {
    return {
      progress: 8,
      title: 'Optimization in progress',
      finished: false,
    };
  }

  const doneCount = countMatching(summary, DONE_STATUS_KEYWORDS);
  const rawProgress = Math.round((doneCount / total) * 100);
  const finished = doneCount >= total;
  const progress = finished ? 100 : Math.max(8, Math.min(98, rawProgress));

  return {
    progress,
    title: `Optimization in progress: ${doneCount} of ${total} items processed`,
    finished,
  };
}
